import { useState, useEffect, useContext, useCallback } from "react";
import { serviceService } from "../../../services/api/serviceService";
import { AuthContext } from "../../../contexts/AuthContext";
import { StatusBadge } from "../../../constants/statusConstants";

const VatConfig = () => {
  const { token } = useContext(AuthContext);
  const [vatConfig, setVatConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [percent, setPercent] = useState("");

  // Hàm fetch cấu hình VAT
  const fetchVatConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await serviceService.get_vat_config(token);

      if (response.success) {
        setVatConfig(response.data);
        setPercent(response.data.percent.toString());
      } else {
        setError("Không thể tải cấu hình VAT");
      }
    } catch (err) {
      setError("Đã xảy ra lỗi khi tải cấu hình VAT");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Hàm cập nhật cấu hình VAT
  const handleUpdateVat = async () => {
    const percentValue = parseFloat(percent);

    if (isNaN(percentValue) || percentValue < 0 || percentValue > 100) {
      setError("Vui lòng nhập tỷ lệ VAT hợp lệ (0-100)");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage("");

      const response = await serviceService.update_vat_config(token, {
        percent: percentValue,
      });

      if (response.success) {
        setSuccessMessage("Cập nhật cấu hình VAT thành công!");
        setVatConfig(response.data);

        // Xóa thông báo sau 3 giây
        setTimeout(() => {
          setSuccessMessage("");
        }, 3000);
      } else {
        setError("Không thể cập nhật cấu hình VAT");
      }
    } catch (err) {
      setError("Đã xảy ra lỗi khi cập nhật cấu hình VAT");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleUpdateVat();
    }
  };

  useEffect(() => {
    fetchVatConfig();
  }, [fetchVatConfig]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          Loader2
          <p className="text-gray-600">Đang tải cấu hình VAT...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center">
      <div className="w-full max-w-xl bg-white rounded-[16px] border border-[#D2D5DA] p-4">
        <div className="bg-white">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            Cấu hình VAT
          </h1>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-sm">{successMessage}</p>
            </div>
          )}

          {vatConfig && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600 mb-1">Trạng thái:</p>
                  <StatusBadge
                    status={vatConfig.active ? "Hoạt động" : "Không hoạt động"}
                    className="min-w-[120px]"
                  />
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Cập nhật lần cuối:</p>
                  <p className="text-gray-900 font-medium">
                    {new Date(vatConfig.updatedAt).toLocaleString("vi-VN")}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <label
              htmlFor="percent"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Tỷ lệ VAT (%)
            </label>
            <div className="relative">
              <input
                type="number"
                id="percent"
                value={percent}
                onChange={(e) => setPercent(e.target.value)}
                onKeyPress={handleKeyPress}
                step="0.01"
                min="0"
                max="100"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                placeholder="Nhập tỷ lệ VAT"
              />
              <span className="absolute right-4 top-2.5 text-gray-500">%</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleUpdateVat}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-default"
            >
              {saving ? <>Đang lưu...</> : <>Cập nhật</>}
            </button>
          </div>
        </div>
      </div>
    </div>


  );
};

export default VatConfig;
